const subscribers = new Set()

export const WEAR_LOG_CHANGE_EVENT = 'fos:wear-log-change'

export function subscribeToWearLogChanges(subscriber) {
  if (typeof window !== 'undefined') {
    const listener = event => subscriber(event.detail)
    window.addEventListener(WEAR_LOG_CHANGE_EVENT, listener)
    return () => window.removeEventListener(WEAR_LOG_CHANGE_EVENT, listener)
  }

  subscribers.add(subscriber)
  return () => subscribers.delete(subscriber)
}

export function publishWearLogChange(change) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WEAR_LOG_CHANGE_EVENT, { detail: change }))
    return
  }

  subscribers.forEach(subscriber => subscriber(change))
}

function compareLogs(left, right) {
  const leftDate = left?.worn_on || left?.wear_date || ''
  const rightDate = right?.worn_on || right?.wear_date || ''
  if (leftDate !== rightDate) return rightDate.localeCompare(leftDate)
  return String(right?.created_at || '').localeCompare(String(left?.created_at || ''))
}

export function applyWearLogChange(logs, change, { limit = 50, fragranceId = null } = {}) {
  if (change.type === 'deleted') {
    return logs.filter(log => log.id !== change.id)
  }

  if (!change.row?.id) return logs

  const existing = logs.find(log => log.id === change.row.id)
  const remaining = logs.filter(log => log.id !== change.row.id)
  if (fragranceId && change.row.fragrance_id !== fragranceId) return remaining

  const row = {
    ...existing,
    ...change.row,
    applications: change.row.applications ?? existing?.applications ?? [],
  }

  return [row, ...remaining].sort(compareLogs).slice(0, limit)
}

export function mergePendingCreatedLogs(logs, pendingRows, options = {}) {
  return [...pendingRows].reduce(
    (current, row) => applyWearLogChange(current, { type: 'created', row }, options),
    logs,
  )
}

function snapshotConfirmsChange(serverRows, change) {
  const serverRow = serverRows.find(row => row.id === (change.id || change.row?.id))
  if (change.type === 'deleted') return !serverRow
  if (change.type === 'created') return Boolean(serverRow)
  if (change.type !== 'updated' || !serverRow) return false

  return Object.entries(change.row || {}).every(([key, value]) => {
    if (key === 'applications') return true
    return serverRow[key] === value
  })
}

/**
 * Reconcile a server read without allowing a stale replica/snapshot to undo a
 * mutation already confirmed and published by FOS-STATE-REFRESH-001.
 */
export function reconcileWearLogSnapshot(serverRows, pendingChanges, options = {}) {
  const remaining = new Map()
  let logs = serverRows

  for (const [id, change] of pendingChanges) {
    if (snapshotConfirmsChange(serverRows, change)) continue
    remaining.set(id, change)
    logs = applyWearLogChange(logs, change, options)
  }

  return { logs, pendingChanges: remaining }
}
